'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { FileUploadArea } from '../ui/file-upload-area';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { X, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Format, FORMAT_MIME_TYPES, getFormatFromPath } from '@/lib/formats';
import { useState, useCallback } from 'react';

interface UploadingFile {
  id: string;
  file: File;
  status: 'uploading' | 'done' | 'error';
  uploadProgress: number;
  tempPath?: string;
}

export interface CreateDatasetFromFilesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateDatasetFromFilesModal({
  open,
  onOpenChange
}: CreateDatasetFromFilesModalProps) {
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const [isCreatingDataset, setIsCreatingDataset] = useState(false);
  const router = useRouter();

  const handleFilesSelected = useCallback((fileList: FileList | null) => {
    if (!fileList) return;
    
    const newFiles = Array.from(fileList).map(file => ({
      id: uuidv4(),
      file,
      status: 'uploading' as const,
      uploadProgress: 0
    }));

    setFiles(prev => [...prev, ...newFiles]);

    // Start uploading each file
    newFiles.forEach(async (fileInfo) => {
      try {
        // Get format from file extension
        const format = getFormatFromPath(fileInfo.file.name);
        
        // Get signed URL for temp storage
        const response = await fetch('/api/convert/urls', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sourceFormat: format,
            destinationFormat: format,
            filename: fileInfo.file.name
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to get upload URL');
        }

        const { source } = await response.json();
        const { uploadUrl, filePath, contentType } = source;

        // Upload file
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
          },
          body: fileInfo.file
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file');
        }

        // Update file status
        setFiles(prev => prev.map(f => 
          f.id === fileInfo.id 
            ? { ...f, status: 'done', uploadProgress: 100, tempPath: filePath }
            : f
        ));
      } catch (error) {
        console.error('Error uploading file:', error);
        setFiles(prev => prev.map(f => 
          f.id === fileInfo.id 
            ? { ...f, status: 'error', uploadProgress: 0 }
            : f
        ));
        toast.error('Failed to upload file');
      }
    });
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const handleCreateDataset = useCallback(async () => {
    try {
      setIsCreatingDataset(true);

      // Get all successfully uploaded files
      const uploadedFiles = files
        .filter(f => f.status === 'done' && f.tempPath)
        .map(f => ({
          name: f.file.name,
          format: getFormatFromPath(f.file.name),
          size: f.file.size,
          tempPath: f.tempPath!
        }));

      if (uploadedFiles.length === 0) {
        throw new Error('No files uploaded successfully');
      }

      // Create dataset
      const response = await fetch('/api/datasets/create-from-files', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            visibility: 'link',
            files: uploadedFiles
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create dataset');
      }

      const dataset = await response.json();
      
      // Close modal and navigate to new dataset
      onOpenChange(false);
      router.push(`/datasets/${dataset.id}/${dataset.slug}`);
      
      toast.success('Dataset created successfully');
    } catch (error) {
      console.error('Error creating dataset:', error);
      toast.error('Failed to create dataset');
    } finally {
      setIsCreatingDataset(false);
    }
  }, [files, onOpenChange, router]);

  const canCreateDataset = files.length > 0 && files.every(f => f.status === 'done');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Dataset from Files</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <FileUploadArea
            acceptedMimeType={[FORMAT_MIME_TYPES[Format.CSV], FORMAT_MIME_TYPES[Format.TSV], FORMAT_MIME_TYPES[Format.PARQUET]].join(',')}
            formatName={['CSV', 'TSV', 'Parquet']}
            multiple={true}
            onFilesSelected={handleFilesSelected}
            buttonText="Select files"
          />

          {files.length > 0 && (
            <div className="space-y-2">
              {files.map(file => (
                <div key={file.id} className="flex items-center gap-2 p-2 border rounded">
                  <FileText className="shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="truncate text-sm">{file.file.name}</p>
                      <button
                        onClick={() => handleRemoveFile(file.id)}
                        className="shrink-0 p-1 hover:bg-secondary rounded"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    {file.status === 'uploading' && (
                      <Progress value={file.uploadProgress} className="h-1" />
                    )}
                    <div className="flex items-center gap-1 mt-1">
                      {file.status === 'uploading' && (
                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      )}
                      {file.status === 'done' && (
                        <CheckCircle className="h-3 w-3 text-green-500" />
                      )}
                      {file.status === 'error' && (
                        <AlertCircle className="h-3 w-3 text-destructive" />
                      )}
                      <span className="text-xs text-muted-foreground">
                        {file.status === 'uploading' && 'Uploading...'}
                        {file.status === 'done' && 'Uploaded'}
                        {file.status === 'error' && 'Failed to upload'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleCreateDataset}
              disabled={!canCreateDataset || isCreatingDataset}
            >
              {isCreatingDataset && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Dataset
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
} 